// @ts-nocheck - Victory Native XL types
import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Dimensions,
  FlatList,
  TextInput,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import {
  getPartnerSplitEarnings,
  PartnerSplitEarnings,
} from '../services/revenue-split-service';
import { getAllPartnerChargerTransactionDetails } from '../services/partner-service';
import { CartesianChart, Bar, PolarChart, Pie, useChartPressState } from 'victory-native';
import { LinearGradient, vec, RoundedRect, useFont, Circle, Text as SKText } from '@shopify/react-native-skia';
import { useDerivedValue, useAnimatedReaction, runOnJS, type SharedValue } from 'react-native-reanimated';
import * as Haptics from 'expo-haptics';
import { SplitStatusBadge } from '../components/RevenueSplit';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '../navigation/stackTypes';

const { width } = Dimensions.get('window');

type NavigationProp = StackNavigationProp<RootStackParamList>;

// Interactive Bar Chart Component
const ToolTip = ({ x, y }: { x: SharedValue<number>, y: SharedValue<number> }) => {
  return (
    <Circle 
      cx={x}
      cy={y}
      r={8}
      color={'#FFFFFF'}
      opacity={0.8}
    />
  );
};

interface MonthlyEarningsBarChartProps {
  data: Array<{ x: number; meander: number; split: number; total: number; label: string }>;
  hasSeparateEarnings: boolean;
  showMeanderLegend: boolean;
  showSplitLegend: boolean;
}

const MonthlyEarningsBarChart: React.FC<MonthlyEarningsBarChartProps> = ({ 
  data, 
  hasSeparateEarnings,
  showMeanderLegend,
  showSplitLegend,
}) => {
  const axisFont = useFont(require('../../assets/fonts/Plus_Jakarta_Sans/static/PlusJakartaSans-Bold.ttf'), 10);
  const toolTipFont = useFont(require('../../assets/fonts/Plus_Jakarta_Sans/static/PlusJakartaSans-Bold.ttf'), 12);

  const { state, isActive } = useChartPressState({
    x: 0,
    y: { meander: 0, split: 0, total: 0 },
  });

  // Get the current value being hovered
  const value = useDerivedValue(() => {
    const idx = Math.round(((state as any)?.x?.value?.value) ?? 0);
    if (idx >= 0 && idx < data.length) {
      const point = data[idx];
      if (hasSeparateEarnings) {
        const meander = point.meander || 0;
        const split = point.split || 0;
        
        // Only show values greater than 0
        const parts: string[] = [];
        if (meander > 0) {
          parts.push(`M: $${meander.toFixed(2)}`);
        }
        if (split > 0) {
          parts.push(`S: $${split.toFixed(2)}`);
        }
        
        // If both are 0, show "No earnings"
        if (parts.length === 0) {
          return 'No earnings';
        }
        
        return parts.join(' | ');
      } else {
        const total = point.total || 0;
        return total > 0 ? `$${total.toFixed(2)}` : 'No earnings';
      }
    }
    return '$0.00';
  }, [state, data, hasSeparateEarnings]);

  // Trigger haptic feedback when dragging to a new data point
  const triggerHaptic = React.useCallback(() => {
    try {
      void Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    } catch {}
  }, []);

  useAnimatedReaction(
    () => {
      return Math.round(((state as any)?.x?.value?.value) ?? 0);
    },
    (currentIndex, previousIndex) => {
      if (isActive && previousIndex !== null && currentIndex !== previousIndex) {
        runOnJS(triggerHaptic)();
      }
    },
    [isActive]
  );

  return (
    <>
      <View style={styles.chartContainer}>
        <CartesianChart
          data={data}
          xKey="x"
          yKeys={hasSeparateEarnings ? ['meander', 'split'] : ['total']}
          padding={15}
          domainPadding={{ top: 20 }}
          chartPressState={state}
          xAxis={{
            labelColor: '#E8E8E9',
            tickCount: data.length,
            formatXLabel: (x) => {
              const idx = Math.round(Number(x));
              return data[idx]?.label || '';
            },
            font: axisFont || undefined,
            labelOffset: 6,
            labelPosition: 'outset',
          }}
          yAxis={[{
            labelColor: '#E8E8E9',
            formatYLabel: (v) => `$${Number(v).toFixed(2)}`,
            yKeys: hasSeparateEarnings ? ['meander', 'split'] : ['total'],
            font: axisFont || undefined,
            labelOffset: 8,
            labelPosition: 'outset',
          }]}
        >
          {({ points, chartBounds }) => {
            return (
              <>
                <RoundedRect
                  x={chartBounds.left}
                  y={chartBounds.top}
                  width={chartBounds.right - chartBounds.left}
                  height={chartBounds.bottom - chartBounds.top}
                  r={10}
                  color={'#1D2025'}
                />
                {isActive && (
                  <SKText 
                    x={chartBounds.left + 10}
                    y={25}
                    font={toolTipFont}
                    text={value}
                    color={'#FFFFFF'}
                    opacity={0.8}
                    style="fill"
                  />
                )}
                {hasSeparateEarnings ? (
                  <>
                    {showMeanderLegend && (
                      <Bar
                        points={points.meander}
                        chartBounds={chartBounds}
                        innerPadding={0.3}
                        barWidth={8}
                        color="#3B82F6"
                        roundedCorners={{ topLeft: 4, topRight: 4 }}
                      />
                    )}
                    {showSplitLegend && (
                      <Bar
                        points={points.split}
                        chartBounds={chartBounds}
                        innerPadding={0.3}
                        barWidth={8}
                        color="#51B868"
                        roundedCorners={{ topLeft: 4, topRight: 4 }}
                      />
                    )}
                  </>
                ) : (
                  <Bar
                    points={points.total}
                    chartBounds={chartBounds}
                    innerPadding={0.3}
                    barWidth={8}
                    color="#51B868"
                    roundedCorners={{ topLeft: 4, topRight: 4 }}
                  />
                )}
                {isActive && (
                  <ToolTip 
                    x={state.x.position} 
                    y={hasSeparateEarnings ? state.y.split.position : state.y.total.position} 
                  />
                )}
              </>
            );
          }}
        </CartesianChart>
      </View>

      {/* Legend */}
      {hasSeparateEarnings && (showMeanderLegend || showSplitLegend) && (
        <View style={styles.chartLegend}>
          {showMeanderLegend && (
            <View style={styles.legendItem}>
              <View style={[styles.legendDot, { backgroundColor: '#3B82F6' }]} />
              <Text style={styles.legendText}>Meander Partner</Text>
            </View>
          )}
          {showSplitLegend && (
            <View style={styles.legendItem}>
              <View style={[styles.legendDot, { backgroundColor: '#51B868' }]} />
              <Text style={styles.legendText}>Revenue Split</Text>
            </View>
          )}
        </View>
      )}
    </>
  );
};

const PartnerEarningsDashboard = () => {
  const navigation = useNavigation<NavigationProp>();
  const user = useSelector((state: RootState) => state.user.userObj);
  const [earnings, setEarnings] = useState<PartnerSplitEarnings | null>(null);
  const [earningsTrend, setEarningsTrend] = useState<Array<{
    month: string;
    meanderPartnerEarnings: number;
    revenueSplitEarnings: number;
    totalEarnings: number;
    orderCount: number;
  }>>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter chargers based on search query (always call useMemo, even if earnings is null)
  const filteredChargers = useMemo(() => {
    if (!earnings?.byCharger) return [];
    if (!searchQuery.trim()) {
      return earnings.byCharger;
    }
    const query = searchQuery.toLowerCase();
    return earnings.byCharger.filter(charger => 
      charger.chargerName.toLowerCase().includes(query) ||
      charger.hostUsername?.toLowerCase().includes(query) ||
      charger.chargerId.toLowerCase().includes(query)
    );
  }, [earnings?.byCharger, searchQuery]);

  // Prepare pie chart data for top 10 chargers by monthly earnings (always call useMemo)
  const topChargers = useMemo(() => {
    if (!earnings?.byCharger) return [];
    return [...earnings.byCharger]
      .sort((a, b) => b.monthlyEarnings - a.monthlyEarnings)
      .slice(0, 10);
  }, [earnings?.byCharger]);

  const pieData = useMemo(() => {
    return topChargers.map((charger, index) => ({
      label: charger.chargerName.length > 15 ? charger.chargerName.substring(0, 15) + '...' : charger.chargerName,
      value: charger.monthlyEarnings,
      color: `hsl(${(index * 360) / topChargers.length}, 70%, 60%)`,
    }));
  }, [topChargers]);

  useEffect(() => {
    fetchEarnings();
  }, []);

  const fetchEarnings = async () => {
    if (!user?.uuid) return;

    try {
      setLoading(true);
      setError(null);
      
      // Fetch both earnings data and transaction details
      const [earningsResponse, transactionResponse] = await Promise.all([
        getPartnerSplitEarnings(user.uuid),
        getAllPartnerChargerTransactionDetails({})
      ]);

      if (earningsResponse.status === 200 && earningsResponse.payload) {
        setEarnings(earningsResponse.payload);
      } else {
        setError(earningsResponse.payload?.message || 'Failed to load earnings');
      }

      // Extract and aggregate earningsTrend from all chargers
      if (transactionResponse.status === 200 && transactionResponse.payload?.chargers) {
        const chargers = transactionResponse.payload.chargers;
        
        if (chargers.length === 1 && chargers[0].earningsTrend) {
          // Single charger - use its trend directly
          setEarningsTrend(chargers[0].earningsTrend);
        } else if (chargers.length > 1) {
          // Multiple chargers - aggregate by month
          const aggregatedTrend = new Map<string, any>();
          
          for (const charger of chargers) {
            if (charger.earningsTrend && Array.isArray(charger.earningsTrend)) {
              for (const monthData of charger.earningsTrend) {
                const existing = aggregatedTrend.get(monthData.month);
                
                if (existing) {
                  existing.meanderPartnerEarnings += monthData.meanderPartnerEarnings || 0;
                  existing.revenueSplitEarnings += monthData.revenueSplitEarnings || 0;
                  existing.totalEarnings += monthData.totalEarnings || 0;
                  existing.orderCount += monthData.orderCount || 0;
                } else {
                  aggregatedTrend.set(monthData.month, { ...monthData });
                }
              }
            }
          }
          
          const aggregatedArray = Array.from(aggregatedTrend.values()).sort((a, b) => 
            a.month.localeCompare(b.month)
          );
          
          setEarningsTrend(aggregatedArray);
        }
      }
    } catch (err) {
      setError('An error occurred while loading earnings');
      console.error('Error fetching earnings:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            onPress={() => navigation.goBack()}
            style={styles.backButton}
          >
            <Ionicons name="arrow-back" size={24} color="#fff" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Partner Earnings</Text>
        </View>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#3B82F6" />
          <Text style={styles.loadingText}>Loading earnings...</Text>
        </View>
      </SafeAreaView>
    );
  }

  if (error) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            onPress={() => navigation.goBack()}
            style={styles.backButton}
          >
            <Ionicons name="arrow-back" size={24} color="#fff" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Partner Earnings</Text>
        </View>
        <View style={styles.errorContainer}>
          <Ionicons name="alert-circle-outline" size={64} color="#F97777" />
          <Text style={styles.errorText}>{error}</Text>
          <TouchableOpacity style={styles.retryButton} onPress={fetchEarnings}>
            <Text style={styles.retryButtonText}>Retry</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  if (!earnings) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            onPress={() => navigation.goBack()}
            style={styles.backButton}
          >
            <Ionicons name="arrow-back" size={24} color="#fff" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Partner Earnings</Text>
        </View>
        <View style={styles.emptyState}>
          <Ionicons name="cash-outline" size={64} color="#8D8E90" />
          <Text style={styles.emptyStateTitle}>No Earnings Data</Text>
          <Text style={styles.emptyStateText}>
            You haven't earned any revenue splits yet
          </Text>
        </View>
      </SafeAreaView>
    );
  }

  const { summary, byCharger, monthlyTrend, recentPayouts } = earnings;

  // Prepare monthly earnings bar chart data from earningsTrend
  const barData = earningsTrend.length > 0
    ? earningsTrend.map((month, index) => {
        const date = new Date(month.month + '-01');
        const monthName = date.toLocaleDateString('en-US', { month: 'short' });
        return {
          x: index,
          meander: month.meanderPartnerEarnings,
          split: month.revenueSplitEarnings,
          total: month.totalEarnings,
          label: monthName,
        };
      })
    : (monthlyTrend && monthlyTrend.length > 0) 
      ? monthlyTrend.map((month, index) => {
          const date = new Date(month.month + '-01');
          const monthName = date.toLocaleDateString('en-US', { month: 'short' });
          return {
            x: index,
            meander: 0,
            split: 0,
            total: month.earnings,
            label: monthName,
          };
        })
      : [];
  
  const hasSeparateEarnings = earningsTrend.length > 0 && 
    earningsTrend.some(t => t.meanderPartnerEarnings > 0 || t.revenueSplitEarnings > 0);
  
  // Check if we should show meander or split in legend (hide if all zeros)
  const totalMeanderEarnings = earningsTrend.reduce((sum, t) => sum + (t.meanderPartnerEarnings || 0), 0);
  const totalSplitEarnings = earningsTrend.reduce((sum, t) => sum + (t.revenueSplitEarnings || 0), 0);
  const showMeanderLegend = totalMeanderEarnings > 0;
  const showSplitLegend = totalSplitEarnings > 0;
  
  // Check if all values are zero
  const hasAnyNonZeroValues = barData.some(d => d.meander !== 0 || d.split !== 0 || d.total !== 0);

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => navigation.goBack()}
          style={styles.backButton}
        >
          <Ionicons name="arrow-back" size={24} color="#fff" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>Partner Earnings</Text>
        <TouchableOpacity onPress={fetchEarnings} style={styles.refreshButton}>
          <Ionicons name="refresh" size={24} color="#fff" />
        </TouchableOpacity>
      </View>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {/* Summary Cards */}
        <View style={styles.summaryCardsContainer}>
          <View style={styles.summaryCard}>
            <View style={styles.summaryCardHeader}>
              <Ionicons name="cash-outline" size={20} color="#51B868" />
              <Text style={styles.summaryCardLabel}>This Month</Text>
            </View>
            <Text style={[styles.summaryCardValue, { color: '#51B868' }]}>
              ${summary.monthlyEarnings.toFixed(2)}
            </Text>
          </View>

          <View style={styles.summaryCard}>
            <View style={styles.summaryCardHeader}>
              <Ionicons name="trending-up-outline" size={20} color="#3B82F6" />
              <Text style={styles.summaryCardLabel}>Total Earned</Text>
            </View>
            <Text style={styles.summaryCardValue}>
              ${summary.totalEarnings.toFixed(2)}
            </Text>
          </View>

          <View style={styles.summaryCard}>
            <View style={styles.summaryCardHeader}>
              <Ionicons name="time-outline" size={20} color="#F59E0B" />
              <Text style={styles.summaryCardLabel}>Pending</Text>
            </View>
            <Text style={styles.summaryCardValue}>
              ${summary.totalPending.toFixed(2)}
            </Text>
          </View>

          <View style={styles.summaryCard}>
            <View style={styles.summaryCardHeader}>
              <Ionicons name="git-network-outline" size={20} color="#3B82F6" />
              <Text style={styles.summaryCardLabel}>Chargers</Text>
            </View>
            <Text style={styles.summaryCardValue}>{summary.chargerCount}</Text>
          </View>
        </View>

        {/* Payout Information */}
        <View style={styles.payoutSection}>
          <View style={styles.payoutHeader}>
            <Text style={styles.sectionTitle}>Payout Information</Text>
          </View>
          <View style={styles.payoutDetails}>
            <View style={styles.payoutRow}>
              <View style={styles.payoutLabelWithIcon}>
                <Ionicons name="checkmark-circle" size={16} color="#10B981" />
                <Text style={styles.payoutLabel}>Total Paid:</Text>
              </View>
              <Text style={[styles.payoutValue, { color: '#10B981', fontWeight: '700' }]}>
                ${summary.totalPaid.toFixed(2)}
              </Text>
            </View>
            <View style={styles.payoutRow}>
              <View style={styles.payoutLabelWithIcon}>
                <Ionicons name="time-outline" size={16} color="#F59E0B" />
                <Text style={styles.payoutLabel}>Pending:</Text>
              </View>
              <Text style={[styles.payoutValue, { color: '#F59E0B', fontWeight: '700' }]}>
                ${summary.totalPending.toFixed(2)}
              </Text>
            </View>
            <View style={styles.payoutRow}>
              <View style={styles.payoutLabelWithIcon}>
                <Ionicons name="lock-closed" size={16} color="#EF4444" />
                <Text style={styles.payoutLabel}>Withheld:</Text>
              </View>
              <Text style={[styles.payoutValue, { color: '#EF4444', fontWeight: '700' }]}>
                ${summary.totalWithheld.toFixed(2)}
              </Text>
            </View>
          </View>
        </View>

        {/* Earnings by Charger - Pie Chart */}
        {byCharger.length > 0 && (
          <View style={styles.chartSection}>
            <Text style={styles.chartTitle}>Earnings by Charger (This Month)</Text>
            {byCharger.length > 10 && (
              <Text style={styles.chartSubtitle}>Showing top 10 earners</Text>
            )}
            <View style={styles.chartContainer}>
              <PolarChart
                padding={6}
                data={pieData}
                labelKey="label"
                valueKey="value"
                colorKey="color"
                containerStyle={{ width: '100%', height: 250, backgroundColor: 'transparent' }}
                canvasStyle={{ backgroundColor: 'transparent' }}
              >
                <Pie.Chart
                  innerRadius={60}
                  padAngle={6}
                  cornerRadius={4}
                  animate={{ type: 'timing', duration: 400 }}
                >
                  {({ slice }) => (
                    <>
                      <Pie.Slice />
                      <Pie.SliceAngularInset
                        angularInset={{ 
                          angularStrokeWidth: 6, 
                          angularStrokeColor: '#1A1D21' 
                        }}
                      />
                    </>
                  )}
                </Pie.Chart>
              </PolarChart>
            </View>
          </View>
        )}

        {/* Charger Details */}
        <View style={styles.listSection}>
          <View style={styles.listHeader}>
            <Text style={styles.sectionTitle}>
              Chargers ({filteredChargers.length}{filteredChargers.length !== byCharger.length ? ` of ${byCharger.length}` : ''})
            </Text>
          </View>
          
          {/* Search Bar */}
          <View style={styles.searchContainer}>
            <Ionicons name="search" size={20} color="#8D8E90" style={styles.searchIcon} />
            <TextInput
              style={styles.searchInput}
              placeholder="Search by name, host, or ID..."
              placeholderTextColor="#8D8E90"
              value={searchQuery}
              onChangeText={setSearchQuery}
            />
            {searchQuery.length > 0 && (
              <TouchableOpacity
                onPress={() => setSearchQuery('')}
                style={styles.clearButton}
              >
                <Ionicons name="close-circle" size={20} color="#8D8E90" />
              </TouchableOpacity>
            )}
          </View>

          {/* Charger List */}
          <FlatList
            data={filteredChargers}
            scrollEnabled={false}
            keyExtractor={(item) => item.chargerId}
            renderItem={({ item: charger, index }) => {
              // Find the index in the original byCharger array to get the correct color
              const originalIndex = byCharger.findIndex(c => c.chargerId === charger.chargerId);
              // Check if this charger is in the top 10
              const isTopCharger = topChargers.some(tc => tc.chargerId === charger.chargerId);
              const topChargerIndex = topChargers.findIndex(tc => tc.chargerId === charger.chargerId);
              const chargerColor = isTopCharger 
                ? pieData[topChargerIndex]?.color 
                : `hsl(${(originalIndex * 360) / byCharger.length}, 70%, 60%)`;
              
              return (
                <View style={styles.chargerCard}>
                  {/* Color indicator */}
                  <View 
                    style={[
                      styles.colorIndicator, 
                      { backgroundColor: chargerColor }
                    ]} 
                  />
                  <View style={styles.chargerRank}>
                    <Text style={styles.rankText}>#{originalIndex + 1}</Text>
                  </View>
                  <View style={styles.chargerInfo}>
                    <Text style={styles.chargerName}>
                      {String(charger.chargerName || 'Unknown')}
                    </Text>
                    <Text style={styles.chargerHost}>
                      Host: {String(charger.hostUsername || 'Unknown')}
                    </Text>
                    <Text style={styles.chargerSplit}>
                      Split: {Number(charger.splitPercentage || 0).toFixed(1)}%
                    </Text>
                  </View>
                  <View style={styles.chargerEarnings}>
                    <Text style={styles.earningsAmount}>
                      ${Number(charger.monthlyEarnings || 0).toFixed(2)}
                    </Text>
                    <Text style={styles.earningsLabel}>this month</Text>
                    <Text style={styles.earningsTotal}>
                      ${Number(charger.totalEarnings || 0).toFixed(2)} total
                    </Text>
                  </View>
                </View>
              );
            }}
            ListEmptyComponent={
              <View style={styles.emptySearchState}>
                <Ionicons name="search-outline" size={48} color="#8D8E90" />
                <Text style={styles.emptySearchText}>No chargers found</Text>
                <Text style={styles.emptySearchSubtext}>Try a different search term</Text>
              </View>
            }
          />
        </View>

        {/* Monthly Earnings Trend */}
        <View style={styles.chartSection}>
          <Text style={styles.chartTitle}>Monthly Earnings Trend</Text>
          {barData.length === 0 ? (
            <View style={{ padding: 20, alignItems: 'center' }}>
              <Text style={{ color: '#8D8E90', fontSize: 14 }}>
                No earnings trend data available
              </Text>
            </View>
          ) : !hasAnyNonZeroValues ? (
            <View style={{ padding: 20, alignItems: 'center' }}>
              <Text style={{ color: '#8D8E90', fontSize: 14 }}>
                No earnings recorded in the last 12 months
              </Text>
            </View>
          ) : (
            <MonthlyEarningsBarChart 
              data={barData} 
              hasSeparateEarnings={hasSeparateEarnings}
              showMeanderLegend={showMeanderLegend}
              showSplitLegend={showSplitLegend}
            />
          )}
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#1A1D21',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#292D33',
    borderBottomWidth: 1,
    borderBottomColor: '#1A1D23',
  },
  backButton: {
    padding: 4,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#fff',
    flex: 1,
    textAlign: 'center',
  },
  refreshButton: {
    padding: 4,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: '#8D8E90',
    fontSize: 16,
    marginTop: 12,
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 30,
  },
  errorText: {
    color: '#F97777',
    fontSize: 16,
    textAlign: 'center',
    marginTop: 16,
    marginBottom: 24,
  },
  retryButton: {
    backgroundColor: '#3B82F6',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  retryButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 30,
  },
  emptyStateTitle: {
    color: '#fff',
    fontSize: 18,
    fontWeight: '700',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyStateText: {
    color: '#8D8E90',
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
  },
  content: {
    flex: 1,
    padding: 16,
  },
  summaryCardsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginBottom: 20,
    gap: 12,
  },
  summaryCard: {
    backgroundColor: '#292D33',
    borderRadius: 12,
    padding: 16,
    width: (width - 44) / 2,
    minHeight: 100,
  },
  summaryCardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 12,
  },
  summaryCardLabel: {
    color: '#E8E8E9',
    fontSize: 12,
    fontWeight: '500',
    flex: 1,
  },
  summaryCardValue: {
    color: '#3B82F6',
    fontSize: 24,
    fontWeight: '700',
  },
  payoutSection: {
    backgroundColor: '#292D33',
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
  },
  payoutHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  sectionTitle: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700',
  },
  payoutDetails: {
    backgroundColor: '#1A1D23',
    borderRadius: 8,
    padding: 12,
  },
  payoutRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 6,
  },
  payoutLabelWithIcon: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  payoutLabel: {
    color: '#8D8E90',
    fontSize: 14,
  },
  payoutValue: {
    color: '#E8E8E9',
    fontSize: 14,
    fontWeight: '600',
  },
  setupPayoutButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#3B82F6',
    borderRadius: 8,
    paddingVertical: 12,
    marginTop: 12,
    gap: 8,
  },
  setupPayoutText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  chartSection: {
    backgroundColor: '#292D33',
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
  },
  chartTitle: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '700',
    marginBottom: 16,
  },
  chartSubtitle: {
    color: '#8D8E90',
    fontSize: 12,
    marginBottom: 12,
    fontStyle: 'italic',
  },
  chartContainer: {
    width: width - 40, // Full width minus padding
    height: 250,
    alignSelf: 'center',
  },
  chartLegend: {
    flexDirection: 'row',
    justifyContent: 'center',
    flexWrap: 'wrap',
    gap: 16,
    marginTop: 12,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  legendDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  legendText: {
    color: '#E8E8E9',
    fontSize: 12,
  },
  listSection: {
    marginBottom: 20,
  },
  listHeader: {
    marginBottom: 12,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#292D33',
    borderRadius: 12,
    paddingHorizontal: 12,
    paddingVertical: 8,
    marginBottom: 12,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    color: '#fff',
    fontSize: 14,
    paddingVertical: 8,
  },
  clearButton: {
    padding: 4,
  },
  chargerCard: {
    backgroundColor: '#292D33',
    borderRadius: 12,
    padding: 12,
    marginBottom: 8,
    flexDirection: 'row',
    alignItems: 'center',
  },
  colorIndicator: {
    width: 4,
    height: '100%',
    borderRadius: 2,
    marginRight: 12,
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
  },
  chargerRank: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#51B868',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  rankText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '700',
  },
  chargerInfo: {
    flex: 1,
  },
  chargerName: {
    color: '#fff',
    fontSize: 14,
    fontWeight: '600',
  },
  chargerHost: {
    color: '#8D8E90',
    fontSize: 11,
    marginTop: 2,
  },
  chargerSplit: {
    color: '#3B82F6',
    fontSize: 11,
    marginTop: 2,
    fontWeight: '600',
  },
  chargerEarnings: {
    alignItems: 'flex-end',
    marginRight: 8,
  },
  earningsAmount: {
    color: '#51B868',
    fontSize: 16,
    fontWeight: '700',
  },
  earningsLabel: {
    color: '#8D8E90',
    fontSize: 10,
    marginTop: 2,
  },
  earningsTotal: {
    color: '#8D8E90',
    fontSize: 10,
    marginTop: 2,
  },
  emptySearchState: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 40,
    paddingHorizontal: 20,
  },
  emptySearchText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
    marginTop: 16,
  },
  emptySearchSubtext: {
    color: '#8D8E90',
    fontSize: 14,
    marginTop: 8,
  },
});

export default PartnerEarningsDashboard;

